import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { uwinAPI } from "../../../services/uwin.api";
import { decryptOpenSSLAES } from "./uwin-decrypt";
import { saveBlobToFile, getBeneficiaryAgeDisplay } from "../../../helpers/common-uwinhelper";

const VaccinationSuccessCard = ({ 
  toggleDrawer, 
  vaccinationData, 
  beneficiaryData,
  onComplete // Add this prop for closing the drawer after download
}) => {
  
  // Add loading state for download
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Extract data from props with fallbacks
  const successData = {
    // From API response or generate fallback
    beneficiaryReferenceId: beneficiaryData?.beneficiary_reference_id || "N/A",
    // From beneficiary data
    name: beneficiaryData?.ben_name || "N/A",
    age: vaccinationData?.memberDetails?.age || getBeneficiaryAgeDisplay(beneficiaryData),
    gender: beneficiaryData?.beneficiary_gender || "N/A",
    
    // Multiple vaccines from vaccination data
    vaccinationDetailsList: vaccinationData?.vaccinationDetailsList || []
  };

  // Helper function to generate filename
  const generateFileName = () => {
    const beneficiaryName = successData.name !== "N/A" ? successData.name.replace(/\s+/g, '_') : "Unknown";
    const currentDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-'); // Convert to DD-MM-YYYY 
    return `Acknowledgement_${beneficiaryName}_${currentDate}.pdf`;
  };

  console.log(beneficiaryData?.beneficiary_ref_id,"beneficiaryData.beneficiary_ref_id")

  // Handle download acknowledgement with enhanced debugging
  const handleDownloadAcknowledgement = async () => {
    try {
      setIsDownloading(true);

      if (!beneficiaryData?.beneficiary_reference_id) {
        toast.error("Beneficiary reference ID not found");
        return;
      }

      const payload = {
        beneficiary_ref_id: beneficiaryData?.beneficiary_reference_id
      };

      console.log("🔥 Downloading with uwinAPI method...");

      // Try using the existing API method first
      try {
        const blob = await uwinAPI.downloadMcpCardAcknowledgement(payload);
        const fileName = `${beneficiaryData?.ben_name}-ReportCard`;
        const success = saveBlobToFile(blob, fileName, "pdf");

        // If this returns actual file data, handle it
        if (success) {
          // Handle successful response here
          toast.success("Acknowledgement downloaded successfully!");
        } else {
          toast.error("Failed to download certificate");
        }
      } catch (error) {
        toast.error("An error occurred during downloading certificate");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsDownloading(false);
    }
  };

  // Debug log for development (remove in production)
  console.log("VaccinationSuccessCard received props:", { 
    vaccinationData, 
    beneficiaryData, 
    successData 
  });

  return (
    <Box sx={{ width: 490, height: "100vh", bgcolor: "white" }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Member Vaccination
            </Typography>
          </Box>
          
          <IconButton onClick={onComplete} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ================= SUCCESS CONTENT ================= */}
      <Box 
        sx={{ 
          p: 3, 
          flex: 1, 
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        {/* Success Icon */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: "success.main",
              mb: 2
            }} 
          />
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ color: "text.primary" }}
          >
            Member Vaccinated Successfully
          </Typography>
        </Box>

        {/* Member Details Card */}
        <Card 
          sx={{ 
            width: "100%", 
            mb: 3,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Beneficiary Reference ID */}
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
              >
                BENEFICIARY REFERENCE ID
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {decryptOpenSSLAES(beneficiaryData?.beneficiary_reference_id)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Name, Age and Gender Row */}
            <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
              <Box sx={{ flex: 2 }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                >
                  NAME
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {successData?.name}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                >
                  AGE
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {successData.age}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                >
                  GENDER
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {successData.gender}
                </Typography>
              </Box>
            </Box>

            {successData.vaccinationDetailsList.length > 0 ? (
              successData.vaccinationDetailsList.map((vaccine, index) => (
                <Box key={index}>
                  <Divider sx={{ my: 2 }} />

                  {/* Vaccine Name */}
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                    >
                      VACCINE NAME
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {vaccine.vaccineName || "N/A"}
                    </Typography>
                  </Box>

                  {/* Vaccine Dose and Date Row */}
                  <Box sx={{ display: "flex", gap: 3, mb: index !== successData.vaccinationDetailsList.length - 1 ? 1 : 0 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                      >
                        VACCINE DOSE
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {vaccine.dose || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }}
                      >
                        DATE OF VACCINATION
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {vaccine.dateOfVaccination || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No vaccination details available
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Download Button */}
        <Box sx={{ width: "100%", mt: "auto", pt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <FileDownloadIcon />}
            onClick={handleDownloadAcknowledgement}
            disabled={isDownloading}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "16px",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
              },
              "&:disabled": {
                opacity: 0.7,
                cursor: "not-allowed"
              }
            }}
          >
            {isDownloading ? "Downloading..." : "Download Acknowledgement"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default VaccinationSuccessCard;